import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import { FIELD_TYPES, SelectOption } from "components/common/FormDialog/types";
import dayjs from "dayjs";
import {
  BACKUP_FREQUENCIES,
  BACKUP_WEEKDAYS,
  BackupScheduleForm,
  backupCron,
  backupScheduleDraft
} from "modules/databases/backupSchedule";
import { DatabaseBackupStatus } from "modules/databases/types";
import { FC } from "react";
import { Schema, mixed, number, string } from "yup";
import { CLIENT_TIME_ZONE } from "../../constants";

const timeZoneIntl = Intl as typeof Intl & {
  supportedValuesOf?: (key: string) => string[];
};
const TIMEZONES: string[] = [
  ...new Set([
    "UTC",
    CLIENT_TIME_ZONE,
    ...(typeof timeZoneIntl.supportedValuesOf === "function"
      ? timeZoneIntl.supportedValuesOf("timeZone")
      : [])
  ])
];
const uses = (values: Record<string, unknown>, frequency: string) =>
  (values.frequency as SelectOption | null)?.value === frequency;
const guidedTime = (values: Record<string, unknown>) =>
  !uses(values, "hourly") && !uses(values, "custom");

export const BackupScheduleDialog: FC<{
  isOpened: boolean;
  status: DatabaseBackupStatus | null;
  onCancel: () => void;
  onConfirm: (data: {
    cron: string;
    retention: string;
    timezone: SelectOption;
  }) => void;
  isSaving?: boolean;
  error?: string | null;
}> = ({ isOpened, status, onCancel, onConfirm, isSaving, error }) => {
  const initial = backupScheduleDraft(
    status?.cron_exist ? status.cron_config : undefined,
    status?.cron_retention || undefined
  );
  const timezone = status?.cron_timezone || CLIENT_TIME_ZONE;
  const timezones = [...new Set([timezone, ...TIMEZONES])].map((value) => ({
    label: value,
    value
  }));
  return (
    <FormDialog
      isOpened={isOpened}
      onCancel={onCancel}
      title="Backup schedule"
      confirmButtonLabel={isSaving ? "Saving..." : "Save schedule"}
      onConfirm={(form: BackupScheduleForm) => {
        if (isSaving) return;
        onConfirm({
          cron: backupCron(form),
          retention:
            form.retentionMode.value === "days"
              ? `${form.retentionDays}d0h0m`
              : form.retention!.trim(),
          timezone: form.timezone
        });
      }}
      fields={[
        {
          name: "frequency",
          label: "Repeat",
          type: FIELD_TYPES.SELECT,
          options: BACKUP_FREQUENCIES,
          defaultValue: initial.frequency,
          helperText: "How often a backup runs.",
          rules: selectOptionSchema,
          onChange: ({ value, fieldValues, setValue }) => {
            if (
              (value as SelectOption | null)?.value === "custom" &&
              (fieldValues.frequency as SelectOption)?.value !== "custom"
            ) {
              const cron = backupCron(
                fieldValues as unknown as BackupScheduleForm
              );
              if (cron) setValue("cron", cron);
            }
          }
        },
        {
          name: "weekday",
          label: "Day of the week",
          type: FIELD_TYPES.SELECT,
          options: BACKUP_WEEKDAYS,
          defaultValue: initial.weekday,
          helperText: "The backup runs once a week on this day.",
          isHidden: (values) => !uses(values, "weekly"),
          rules: (
            selectOptionSchema.default(undefined) as Schema<SelectOption>
          ).when("frequency", {
            is: (value: SelectOption) => value?.value === "weekly",
            then: (schema: Schema<SelectOption>) =>
              schema.required() as Schema<SelectOption>,
            otherwise: (schema: Schema<SelectOption>) =>
              schema.notRequired() as Schema<SelectOption>
          })
        },
        {
          name: "monthDay",
          label: "Day of the month",
          type: FIELD_TYPES.NUMBER,
          defaultValue: initial.monthDay,
          min: 1,
          max: 31,
          helperText: "Days 29–31 skip months that do not have that day.",
          isHidden: (values) => !uses(values, "monthly"),
          rules: number().when("frequency", {
            is: (value: SelectOption) => value?.value === "monthly",
            then: (schema) => schema.required().integer().min(1).max(31),
            otherwise: (schema) => schema.notRequired()
          })
        },
        {
          name: "timeHeading",
          label: "Time",
          type: FIELD_TYPES.DIVIDER,
          isHidden: (values) => !guidedTime(values)
        },
        {
          name: "time",
          label: "Backup time",
          labelPicker: "Backup time (in the timezone below)",
          type: FIELD_TYPES.TIME_PICKER,
          defaultValue: initial.time,
          isHidden: (values) => !guidedTime(values),
          rules: mixed().when("frequency", {
            is: (value: SelectOption) =>
              value?.value !== "hourly" && value?.value !== "custom",
            then: (schema) =>
              schema
                .required()
                .test(
                  "time",
                  "Choose a valid time.",
                  (value) => dayjs.isDayjs(value) && value.isValid()
                ),
            otherwise: (schema) => schema.notRequired()
          })
        },
        {
          name: "minute",
          label: "Minute past each hour",
          type: FIELD_TYPES.NUMBER,
          defaultValue: initial.minute,
          min: 0,
          max: 59,
          isHidden: (values) => !uses(values, "hourly"),
          rules: number().when("frequency", {
            is: (value: SelectOption) => value?.value === "hourly",
            then: (schema) => schema.required().integer().min(0).max(59),
            otherwise: (schema) => schema.notRequired()
          })
        },
        {
          name: "cron",
          label: "Cron expression",
          type: FIELD_TYPES.TEXT,
          defaultValue: initial.cron,
          helperText:
            "Five fields: minute, hour, day of month, month, day of week.",
          isHidden: (values) => !uses(values, "custom"),
          rules: string().when("frequency", {
            is: (value: SelectOption) => value?.value === "custom",
            then: (schema) =>
              schema
                .required()
                .test(
                  "cron",
                  "Enter a five-field cron expression.",
                  (value) => value.trim().split(/\s+/).length === 5
                ),
            otherwise: (schema) => schema.notRequired()
          })
        },
        {
          name: "timezone",
          label: "Timezone",
          type: FIELD_TYPES.SELECT,
          defaultValue: { label: timezone, value: timezone },
          options: timezones,
          rules: selectOptionSchema,
          helperText:
            "The selected local time follows daylight-saving changes in this timezone."
        },
        {
          name: "retentionMode",
          label: "Keep backups for",
          type: FIELD_TYPES.SELECT,
          defaultValue: initial.retentionMode,
          options: [
            { label: "Days", value: "days" },
            { label: "Custom duration", value: "custom" }
          ],
          rules: selectOptionSchema
        },
        {
          name: "retentionDays",
          label: "Retention in days",
          type: FIELD_TYPES.NUMBER,
          min: 1,
          max: 99,
          defaultValue: initial.retentionDays,
          isHidden: (values) =>
            (values.retentionMode as SelectOption)?.value !== "days",
          rules: number().when("retentionMode", {
            is: (value: SelectOption) => value?.value === "days",
            then: (schema) => schema.required().integer().min(1).max(99),
            otherwise: (schema) => schema.notRequired()
          })
        },
        {
          name: "retention",
          label: "Retention duration",
          type: FIELD_TYPES.TEXT,
          defaultValue: initial.retention,
          helperText: "For example, 7d12h0m. Maximum: 99d23h59m.",
          isHidden: (values) =>
            (values.retentionMode as SelectOption)?.value !== "custom",
          rules: string().when("retentionMode", {
            is: (value: SelectOption) => value?.value === "custom",
            then: (schema) =>
              schema
                .required()
                .matches(
                  /^(?:[0-9]|[1-9][0-9])[dD](?:[0-1]?[0-9]|2[0-3])[hH][0-5]?[0-9][mM]$/,
                  "Use 0–99 days, 0–23 hours and 0–59 minutes, such as 7d0h0m."
                ),
            otherwise: (schema) => schema.notRequired()
          })
        },
        {
          name: "error",
          label: error ?? "",
          type: FIELD_TYPES.NOTES,
          isHidden: () => !error
        }
      ]}
    />
  );
};
